import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
  browseOneDrive,
  linkOneDriveFolder,
  resolveOneDriveShare,
  type OneDriveBrowseItem,
} from "../../services/onedrive.service";
import { fetchClientsForMentions, labelClientsForPicker } from "../../services/clients.service";

/** Size caption for a file row. Omitted rather than shown as "0 B" when unknown. */
const formatSize = (bytes?: number): string => {
  if (typeof bytes !== "number" || bytes < 0) return "File";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
};

interface Crumb {
  itemId?: string;
  name: string;
  /** Carried so drilling into a shared folder keeps resolving against its owner's drive. */
  driveId?: string;
}

interface LinkOneDriveFolderDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The client to attach the link to, when the current location implies one.
   * Omitted above any client in Shared Files, where the dialog offers a picker
   * — but choosing is optional: with no client the link lands in `parentId`.
   */
  clientId?: string;
  /**
   * Destination folder when no client is chosen. Required for the dialog to be
   * usable above a client; without either, there is nowhere to put the link.
   */
  parentId?: string;
  /** Called after a successful link so the explorer can refetch. */
  onLinked: () => void;
}

/**
 * Drill-down browser over the advisor's OWN OneDrive.
 *
 * Folders and individual files are both linkable. A folder keeps picking up
 * whatever is added to it later; a single file is what a colleague usually
 * shares, and Prospo already lets a file sit directly under a client with no
 * folder around it.
 */
export default function LinkOneDriveFolderDialog({
  open,
  onClose,
  clientId,
  parentId,
  onLinked,
}: LinkOneDriveFolderDialogProps) {
  const [items, setItems] = useState<OneDriveBrowseItem[]>([]);
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ name: "OneDrive" }]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionIssue, setConnectionIssue] = useState<"connect" | "reconnect" | null>(null);
  /**
   * Distinguishes "we listed this folder and it holds nothing" from "we never
   * managed to list it". Without this the failure path renders the empty state,
   * which tells the user the folder is empty and invites them to link it — a
   * confident claim about a folder we could not read at all.
   */
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<OneDriveBrowseItem | null>(null);
  const [query, setQuery] = useState("");
  /**
   * The paste-a-link path. It exists because browsing genuinely cannot reach a
   * folder shared from a SharePoint site — it is in nobody's OneDrive, and
   * Microsoft does not list it among "shared with me" either, even though the
   * OneDrive website shows it. A resolved share replaces the browse selection.
   */
  const [shareUrl, setShareUrl] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<OneDriveBrowseItem | null>(null);

  // Only used when no clientId was supplied. Loaded once the dialog opens
  // rather than on mount, so the common case (a client already in scope) costs
  // no request at all.
  const [clients, setClients] = useState<{ id: string; label: string }[]>([]);
  const [pickedClientId, setPickedClientId] = useState<string>("");
  const targetClientId = clientId || pickedClientId;

  const load = useCallback(async (itemId?: string, driveId?: string) => {
    setLoading(true);
    setError(null);
    setConnectionIssue(null);
    setLoaded(false);
    setSelected(null);
    // Navigating away means the user has gone back to browsing, so a folder
    // resolved from a link is no longer what they are pointing at.
    setResolved(null);
    // A filter typed for one folder is meaningless in the next one, and leaving
    // it applied would make the new folder look emptier than it is.
    setQuery("");
    try {
      const response = await browseOneDrive(itemId, driveId);
      setItems(response.items || []);
      setLoaded(true);
    } catch (err: any) {
      // Connect and Reconnect are different actions, so they get different
      // messages — a connection that has expired is not a missing one.
      const code = err?.data?.code;
      if (code === "MICROSOFT_NOT_CONNECTED") {
        setConnectionIssue("connect");
      } else if (code === "MICROSOFT_NEEDS_RECONNECT") {
        setConnectionIssue("reconnect");
      } else {
        setError(err?.data?.message || err?.message || "Could not read your OneDrive.");
      }
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCrumbs([{ name: "OneDrive" }]);
    setShareUrl("");
    setResolved(null);
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open || clientId) return;
    setPickedClientId("");
    // Best-effort: failing to load the list leaves the picker empty and the
    // Link button disabled, which is a clearer dead end than an error banner
    // over a dialog whose main job (browsing) still works.
    fetchClientsForMentions()
      .then((rows) => setClients(labelClientsForPicker(rows || [])))
      .catch(() => setClients([]));
  }, [open, clientId]);

  const handleResolveShare = async () => {
    if (!shareUrl.trim()) return;
    setResolving(true);
    setError(null);
    setResolved(null);
    try {
      const share = await resolveOneDriveShare(shareUrl.trim());
      // A resolved share is the target, so clear any browse selection rather
      // than leaving two things that both look chosen.
      setSelected(null);
      setResolved({
        itemId: share.itemId,
        driveId: share.driveId,
        name: share.name,
        isFolder: share.isFolder,
        childCount: share.childCount,
        mimeType: share.mimeType,
        size: share.size,
        webUrl: share.webUrl,
        isShared: true,
      });
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === "MICROSOFT_NOT_CONNECTED") {
        setConnectionIssue("connect");
      } else if (code === "MICROSOFT_NEEDS_RECONNECT") {
        setConnectionIssue("reconnect");
      } else {
        // The server's messages here are specific — wrong link, a file rather
        // than a folder, no access — so prefer them over anything generic.
        setError(err?.data?.message || err?.message || "Could not open that link.");
      }
    } finally {
      setResolving(false);
    }
  };

  const openFolder = (item: OneDriveBrowseItem) => {
    setCrumbs((prev) => [...prev, { itemId: item.itemId, name: item.name, driveId: item.driveId }]);
    load(item.itemId, item.driveId);
  };

  const jumpToCrumb = (index: number) => {
    const next = crumbs.slice(0, index + 1);
    setCrumbs(next);
    const crumb = next[next.length - 1];
    load(crumb.itemId, crumb.driveId);
  };

  const visibleItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => item.name.toLowerCase().includes(needle));
  }, [items, query]);

  const currentFolder = crumbs[crumbs.length - 1];
  // Precedence: a folder resolved from a pasted link wins, because pasting is
  // an explicit act that happens after any browsing. Then an explicit
  // selection. Then the folder you are inside — linking that is as natural as
  // picking one from the list, and it is the only way to link a folder with no
  // parent listing.
  const target =
    resolved ??
    selected ??
    (currentFolder.itemId
      ? ({ itemId: currentFolder.itemId, name: currentFolder.name, driveId: currentFolder.driveId, isFolder: true } as OneDriveBrowseItem)
      : null);

  const handleLink = async () => {
    if (!target) return;
    setLinking(true);
    setError(null);
    try {
      // With no client chosen the link lands in the folder the user is looking
      // at — the server takes whichever of the two it is given.
      await linkOneDriveFolder(
        target.itemId,
        targetClientId || undefined,
        target.driveId,
        targetClientId ? undefined : parentId,
      );
      onLinked();
      onClose();
    } catch (err: any) {
      const code = err?.data?.code;
      if (code === "NAME_IN_USE") {
        setError(
          `This client already has something called "${target.name}". Rename it in OneDrive, or remove the existing one first.`,
        );
      } else if (code === "MICROSOFT_NOT_CONNECTED") {
        setConnectionIssue("connect");
      } else if (code === "MICROSOFT_NEEDS_RECONNECT") {
        setConnectionIssue("reconnect");
      } else {
        setError(err?.data?.message || err?.message || "Could not link that folder.");
      }
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onClose={linking ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CloudOutlinedIcon color="primary" />
        Link a OneDrive folder or file
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {connectionIssue === "connect" ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Connect your Microsoft account under Settings → Integrations to browse OneDrive.
          </Alert>
        ) : connectionIssue === "reconnect" ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your Microsoft connection needs reconnecting before Prospo can read your OneDrive.
            Go to Settings → Integrations and reconnect — if you connected before file access
            was added, this is also what grants it.
          </Alert>
        ) : (
          error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )
        )}

        {/* Shown only when the current location implies no client. Choosing one
            sends the link to that client's Files root; leaving it blank puts it
            in the folder being viewed. */}
        {!clientId && (
          <Autocomplete
            options={clients}
            size="small"
            sx={{ mb: 1.5 }}
            value={clients.find((c) => c.id === pickedClientId) || null}
            onChange={(_event, option) => setPickedClientId(option?.id || "")}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client (optional)"
                placeholder="Attach to a client, or leave blank"
                helperText="Leave blank to link it into the folder you're viewing instead."
              />
            )}
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {/* Without a client it is not "this client's" files — it goes into a
              shared folder, which is a wider audience, so say so. */}
          What you pick becomes visible to{" "}
          {targetClientId ? "anyone with access to this client" : "anyone who can see this folder"},
          and readable by the Prospo assistant. Prospo never changes anything in your OneDrive.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Filter this folder by name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={!loaded || items.length === 0}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Below the filter, above the listing: a folder shared from a
            SharePoint site cannot appear in the list at all, so the way to
            reach it has to be visible without hunting for it. */}
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Paste a sharing link"
            value={shareUrl}
            onChange={(event) => setShareUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleResolveShare();
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            onClick={handleResolveShare}
            disabled={!shareUrl.trim() || resolving}
            sx={{ whiteSpace: "nowrap" }}
          >
            {resolving ? "Opening…" : "Open"}
          </Button>
        </Stack>

        {/* Says out loud that the list is not the whole story. A folder shared
            from a SharePoint site never appears in it, and the user has no way
            to tell that apart from looking in the wrong place — so without this
            the honest conclusion is that Prospo is broken. */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, mb: 1.5 }}>
          Can't find a folder or file someone shared with you? Paste its link above.
        </Typography>

        {resolved && (
          <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setResolved(null)}>
            Found <strong>{resolved.name}</strong>
            {resolved.isFolder && typeof resolved.childCount === "number"
              ? ` — ${resolved.childCount} item${resolved.childCount === 1 ? "" : "s"}.`
              : !resolved.isFolder
              ? ` — ${formatSize(resolved.size)}.`
              : "."}{" "}
            Linking this rather than anything selected below.
          </Alert>
        )}

        <Breadcrumbs sx={{ mb: 1.5 }}>
          {crumbs.map((crumb, index) =>
            index === crumbs.length - 1 ? (
              <Typography key={index} variant="body2" fontWeight={600} color="text.primary">
                {crumb.name}
              </Typography>
            ) : (
              <Link
                key={index}
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => jumpToCrumb(index)}
              >
                {crumb.name}
              </Link>
            ),
          )}
        </Breadcrumbs>

        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, height: 320, overflow: "hidden", display: "flex", flexDirection: "column" }}
        >
          {loading ? (
            <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
              <CircularProgress />
            </Box>
          ) : !loaded ? (
            /* The listing failed. Say nothing about what the folder contains —
               we do not know, and the alert above already explains why. */
            <Box sx={{ flex: 1, display: "grid", placeItems: "center", textAlign: "center", p: 3 }}>
              <Box>
                <CloudOffOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                <Typography fontWeight={600} sx={{ mt: 1 }}>
                  Couldn't read your OneDrive
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nothing is shown because the folder could not be opened.
                </Typography>
              </Box>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ flex: 1, display: "grid", placeItems: "center", textAlign: "center", p: 3 }}>
              <Box>
                <FolderOutlinedIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                <Typography fontWeight={600} sx={{ mt: 1 }}>
                  Nothing here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This folder is empty. You can still link it.
                </Typography>
              </Box>
            </Box>
          ) : visibleItems.length === 0 ? (
            /* Filtered to nothing — distinct from an empty folder, since the
               folder plainly has contents and the user can clear the filter. */
            <Box sx={{ flex: 1, display: "grid", placeItems: "center", textAlign: "center", p: 3 }}>
              <Box>
                <SearchIcon sx={{ fontSize: 36, color: "text.disabled" }} />
                <Typography fontWeight={600} sx={{ mt: 1 }}>
                  No matches
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nothing in this folder matches "{query.trim()}".
                </Typography>
                <Button size="small" onClick={() => setQuery("")} sx={{ mt: 1 }}>
                  Clear filter
                </Button>
              </Box>
            </Box>
          ) : (
            <Stack
              divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}
              sx={{ flex: 1, overflow: "auto" }}
            >
              {visibleItems.map((item) => {
                const isSelected = selected?.itemId === item.itemId;
                return (
                  <Box
                    key={item.itemId}
                    role="button"
                    tabIndex={0}
                    /* Files are selectable too now. Only folders open, so a
                       double-click or Enter on a file does nothing rather than
                       trying to descend into it. */
                    onClick={() => setSelected(isSelected ? null : item)}
                    onDoubleClick={() => item.isFolder && openFolder(item)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      if (item.isFolder) openFolder(item);
                      else setSelected(isSelected ? null : item);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 2,
                      py: 1.25,
                      cursor: "pointer",
                      bgcolor: isSelected ? "action.selected" : "transparent",
                      borderLeft: "3px solid",
                      borderLeftColor: isSelected ? "primary.main" : "transparent",
                      "&:hover": {
                        bgcolor: isSelected ? "action.selected" : "action.hover",
                      },
                    }}
                  >
                    {item.isFolder ? (
                      <FolderOutlinedIcon color="primary" />
                    ) : (
                      <InsertDriveFileOutlinedIcon color="action" />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={item.isFolder ? 600 : 400} noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.isFolder
                          ? `${item.childCount ?? 0} item${(item.childCount ?? 0) === 1 ? "" : "s"}`
                          : formatSize(item.size)}
                        {/* A shared item lives in someone else's drive, so it
                            keeps working only while they keep sharing it. */}
                        {item.isShared && " · Shared with you"}
                      </Typography>
                    </Box>
                    {item.isFolder && (
                      <Button size="small" onClick={(event) => { event.stopPropagation(); openFolder(item); }}>
                        Open
                      </Button>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={linking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          /* Nothing loaded means nothing verified — do not offer to link a
             folder we could not read, whatever the reason. A resolved share is
             verified by its own round trip, so it does not need the listing. */
          disabled={!target || (!targetClientId && !parentId) || linking || (!loaded && !resolved)}
          startIcon={linking ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {linking ? "Linking…" : target ? `Link "${target.name}"` : "Select a folder or file"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
